import React, { useState, useEffect } from 'react';

import {
    Accordion,
    AccordionContent,
    AccordionTitle,
    RadioWithLabel,
    Typography,
    QuickFilterButton,
    Counter,
} from '@hpstellar/core';
import { ButtonIcon } from '@hpstellar/core/visId';
import { X } from '@hpstellar/vis-icons';

import LeftRailBanner from './left-rail-banner';
import PriceFacet from './vwa-price-facet';
import FacetAccordion from './vwa-facet-accordion';
import { clickOnEnter } from '../../ui/util';
import { getFacetAddons } from '../util';
import FilterNotice from './filter-notice';
import VWAMobileSort from './vwa-mobile-sort';

import './vwa-left-menu.less';

const emptyRangeValue = ['', ''];

const isZeroCountVisible = filters => {
    let showZeroCount = true;
    if (filters && Object.keys(filters).length === 0) {
        showZeroCount = false;
    }
    if (filters && Object.keys(filters).length === 1 && filters.subcat) {
        showZeroCount = Object.keys(filters.subcat).length > 1;
    }
    return showZeroCount;
};
export default function VWALeftMenu({
    facets,
    aligned,
    expandedFacets,
    onSelectFacet,
    sortValues,
    sortValue,
    onSort,
    vanityUrl,
    filters,
    addOnFacets,
    presetRange,
    setPresetRange,
    priceFilter,
    disableDefaultPriceRange,
    visId,
    interstitial = false,
    onCloseInterstitial,
    fullWidth = false,
    isMobile = false,
    quickFilters,
    onClickQuickFilter,
    isSearch = false,
    notice = false,
}) {
    const addOnFacetMap = getFacetAddons(vanityUrl, addOnFacets, filters);
    const { priceFacet: priceFacets } = addOnFacetMap;

    const [range, setRange] = useState(() =>
        filters?.Price ? Object.keys(filters.Price)?.[0]?.split('-') : emptyRangeValue,
    );
    const rangeEmpty = range.every(i => i === '');
    const expanded =
        Array.isArray(expandedFacets) && expandedFacets.reduce((a, b) => ({ ...a, [b.toLowerCase()]: true }), {});

    const facetList = [...facets];
    if (priceFacets.enabled) facetList.splice(priceFacets.displaySequence - 1, 0, priceFacets);

    useEffect(() => {
        setRange(filters?.Price ? Object.keys(filters.Price)?.[0]?.split('-') : emptyRangeValue);
    }, [filters?.Price]);

    const onSubmitRange = () => {
        if (range[0] === '' && range[1] === '') return;

        const rangeValue = [range[0] || 0, range[1] || 99999];
        const rangeString = rangeValue.join('-');

        const isValid = rangeValue[0] >= 0 && rangeValue[1] > 0 && rangeValue[0] < rangeValue[1];
        if (!isValid || (filters.Price && filters.Price[rangeString])) return;

        setPresetRange([]);
        onSelectFacet('Price', { selection: [{ value: rangeString }] })();
    };

    const onSelectPresetRange = (value, selectedIndex) => {
        setRange(emptyRangeValue);

        const index = presetRange.indexOf(selectedIndex);
        let range;
        if (index < 0) {
            range = [...presetRange, selectedIndex];
        } else {
            presetRange.splice(index, 1);
            range = [...presetRange];
        }
        setPresetRange(range);
        onSelectFacet('Price', { selection: range.map(i => priceFacets.value[i]) })();
    };

    const clearRange = () => {
        setRange(emptyRangeValue);
        onSelectFacet('Price', undefined)();
    };

    const showZeroCount = isSearch ? isZeroCountVisible(filters) : false;

    return (
        <div className={`leftMenu${fullWidth ? ' fullWidth' : ''}`}>
            {notice && <FilterNotice notice={notice} />}
            {
                Array.isArray(sortValues) && (
                    <VWAMobileSort 
                        sortValues={sortValues}
                        clickOnEnter={clickOnEnter}
                        onSort={onSort}
                        sortValue={sortValue}
                    />
                )
            }
            {interstitial && isMobile && (
                <>
                    <div className="interstitial-menu-bar">
                        <ButtonIcon
                            size="small"
                            hasBorder={false}
                            icon={<X size="s" />}
                            onClick={() => onCloseInterstitial()}
                        />
                        <Counter className="counter" count={0} size="large" subsetDelimiter="of">
                            {`Filters Applied`}
                        </Counter>
                    </div>
                    <Accordion className="interstitialLeftMenuQuickFilter" size="small" defaultExpanded>
                        <AccordionTitle
                            id="idtitle"
                            onKeyUp={clickOnEnter}
                            tag="h4"
                            title="QUICK FILTERS"
                            className="radio-option-title"
                        />
                        <AccordionContent className="interstitialLeftMenuQuickFilter-content">
                            {quickFilters.map((q, i) => (
                                <QuickFilterButton onClick={() => onClickQuickFilter(q, i)} key={q.label}>
                                    {q.label}
                                </QuickFilterButton>
                            ))}
                        </AccordionContent>
                    </Accordion>
                </>
            )}
            {facetList
                .map(facet => ({ ...facet, enabled: true, displaySequence: 0, ...addOnFacetMap[facet.key] }))
                .sort((a, b) => parseInt(b.displaySequence, 10) - parseInt(a.displaySequence, 10))
                .map((facet, idx) => {
                    if (!facet.enabled) return null;
                    if (facet?.hide) return null;
                    if (facet?.labelOverride) facet.displayName = facet?.labelOverride;

                    if (facet.key === 'priceFacet') {
                        const title = 'PRICE RANGE';
                        return priceFilter
                            ? priceFilter
                            : !disableDefaultPriceRange && (
                                  <Accordion
                                      className="accordion"
                                      title={title}
                                      size="small"
                                      defaultExpanded={facet.expanded}
                                      key={idx}
                                  >
                                      <AccordionTitle
                                          id="idtitle"
                                          onKeyUp={clickOnEnter}
                                          tag="h4"
                                          title={title}
                                          className="radio-option-title"
                                      />
                                      <AccordionContent className="accordionContent">
                                          <PriceFacet
                                              setRange={setRange}
                                              clearRange={clearRange}
                                              priceFacets={priceFacets.value}
                                              onSelectPresetRange={onSelectPresetRange}
                                              range={range}
                                              presetRange={presetRange}
                                              onSubmitRange={onSubmitRange}
                                              rangeEmpty={rangeEmpty}
                                          />
                                      </AccordionContent>
                                  </Accordion>
                              );
                    }

                    return (
                        <FacetAccordion
                            facet={facet}
                            idx={idx}
                            key={idx}
                            expanded={expanded}
                            onSelectFacet={onSelectFacet}
                            aligned={aligned}
                            showZeroCount={showZeroCount}
                            visId={visId}
                            hideEmpty
                        />
                    );
                })}
            <LeftRailBanner />
        </div>
    );
}
